import { useState } from "react";
import { Download, FileText, FileSignature, ShieldCheck, ListChecks, Eye, ChevronDown, ChevronUp, Building2, Users, Layers, History, CheckCircle2 } from "lucide-react";

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || "";
// Los archivos están servidos por el frontend (carpeta public/evidencias)
const FRONT_BASE = window.location.origin;

const DOC = (filename) => `${FRONT_BASE}/evidencias/${filename}`;

const HITOS = [
  {
    id: "H1",
    name: "Hito 1 · Base Técnica y Arquitectura",
    status: "ENTREGADO",
    statusColor: "#10B981",
    monto: "USD 540",
    duracion: "1 semana",
    contrato: "CONT-2026-05-0002 · Anexos C y D",
    descripcion:
      "Establece la columna vertebral técnica de la plataforma: arquitectura modular, modelo de datos, repositorio inicial y autenticación operativa con JWT y bcrypt.",
    entregables: [
      { icon: Building2, label: "Arquitectura técnica base (frontend + backend)", status: "ok" },
      { icon: Layers, label: "Modelo de datos inicial (MongoDB Atlas)", status: "ok" },
      { icon: FileText, label: "Setup de repositorio", status: "ok" },
      { icon: ShieldCheck, label: "Flujo base de autenticación", status: "ok" },
    ],
    documentos: [
      {
        titulo: "Acta de Entrega · Hito 1",
        descripcion:
          "Documento contractual con portada, índice, descripción de los 4 entregables, capturas, mapa de verificación contra Anexo D y acta de aceptación firmable.",
        url: DOC("entrega_hito1_neuromatematicas_jasq.pdf"),
        size: "≈ 2 MB",
        tipo: "PDF · Entrega contractual",
        principal: true,
      },
      {
        titulo: "Dossier QA · B1 · Gestión de Usuarios (M1)",
        descripcion:
          "Evidencia visual del CRUD de usuarios, wizard adaptativo por rol, avatares y auditoría.",
        url: DOC("evidencias_qa_0001_h1_okr1_b1.pdf"),
        size: "≈ 1 MB",
        tipo: "PDF · QA",
      },
      {
        titulo: "Dossier QA · B2 · Gestión de Franquicias (M14)",
        descripcion:
          "Evidencia visual del wizard de franquicias, Sede Principal, scope multi-tenant y auditoría.",
        url: DOC("evidencias_qa_0001_h1_okr1_b2.pdf"),
        size: "≈ 1 MB",
        tipo: "PDF · QA",
      },
      {
        titulo: "Plan macro 8 semanas",
        descripcion:
          "Hoja de ruta general con OKRs, KRs, hitos 2 a 7 y matriz de testing.",
        url: DOC("Plan_8_Semanas_NEUROMATEMATICAS_JASQ.pdf"),
        size: "≈ 30 KB",
        tipo: "PDF · Plan",
      },
      {
        titulo: "Plan detallado · Backlog técnico y funcional",
        descripcion:
          "Cada item del backlog desglosado en tareas funcionales, backend, frontend, QA y DoD. Incluye KRs medibles por OKR.",
        url: DOC("Plan_Detallado_Backlog_NEUROMATEMATICAS_JASQ.pdf"),
        size: "≈ 45 KB",
        tipo: "PDF · Backlog",
      },
    ],
    criterios: [
      "Documento técnico entregado",
      "Repositorio accesible",
      "Login básico funcional",
    ],
  },
  {
    id: "H2",
    name: "Hito 2 · MVP Funcional (Core del Negocio)",
    status: "EN PLANIFICACIÓN",
    statusColor: "#F5B82E",
    monto: "Pendiente",
    duracion: "2 semanas",
    contrato: "CONT-2026-05-0002 · Anexos C y D",
    descripcion:
      "Diagnóstico emocional (M2) y diagnóstico académico (M3). Inicio del ciclo cognitivo del estudiante.",
    entregables: [
      { icon: ListChecks, label: "Banco de preguntas Likert y académicas", status: "todo" },
      { icon: Users, label: "Test emocional autoaplicado + modo entrevista 1:1", status: "todo" },
      { icon: ListChecks, label: "Test académico con 5 áreas y 8 preguntas", status: "todo" },
      { icon: History, label: "Auditoría de diagnósticos", status: "todo" },
    ],
    documentos: [],
    criterios: ["Por definir tras inicio del hito"],
  },
];

function DocCard({ doc }) {
  return (
    <div
      data-testid={`qa-doc-${doc.titulo.replace(/\s+/g, "-").toLowerCase()}`}
      className={`group flex items-start gap-3 p-4 rounded-2xl border transition-all ${
        doc.principal
          ? "border-[#F5B82E] bg-gradient-to-br from-[#FFFAEC] to-white shadow-sm"
          : "border-[#E1E5F0] bg-white hover:border-[#2C49A0]"
      }`}
    >
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          doc.principal ? "bg-[#F5B82E] text-[#1F2547]" : "bg-[#1F2547] text-white"
        }`}
      >
        {doc.principal ? <FileSignature size={20} /> : <FileText size={20} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-bold text-[#1F2547]">{doc.titulo}</p>
          {doc.principal && (
            <span className="text-[10px] font-bold text-[#1F2547] bg-[#F5B82E] px-2 py-0.5 rounded-full">
              PRINCIPAL
            </span>
          )}
        </div>
        <p className="text-xs text-[#6E7591] mt-1 leading-relaxed">{doc.descripcion}</p>
        <div className="flex items-center gap-2 mt-2 text-[11px] text-[#6E7591]">
          <span className="font-semibold text-[#2C49A0]">{doc.tipo}</span>
          <span>·</span>
          <span>{doc.size}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 items-end shrink-0">
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`qa-view-${doc.titulo.replace(/\s+/g, "-").toLowerCase()}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-[#2C49A0] border border-[#2C49A0] hover:bg-[#2C49A0] hover:text-white transition-colors"
        >
          <Eye size={13} />
          Ver
        </a>
        <a
          href={doc.url}
          download
          data-testid={`qa-download-${doc.titulo.replace(/\s+/g, "-").toLowerCase()}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-[#1F2547] text-white hover:bg-[#2C49A0] transition-colors"
        >
          <Download size={13} />
          Descargar
        </a>
      </div>
    </div>
  );
}

function HitoSection({ hito, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const isEntregado = hito.status === "ENTREGADO";

  return (
    <section
      data-testid={`qa-hito-${hito.id.toLowerCase()}`}
      className="bg-white border border-[#E1E5F0] rounded-3xl overflow-hidden shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`qa-hito-toggle-${hito.id.toLowerCase()}`}
        className="w-full flex items-center gap-4 p-5 md:p-6 hover:bg-[#FAFBFE] transition-colors text-left"
      >
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-white font-black text-lg"
          style={{ background: isEntregado ? "#1F2547" : "#6E7591" }}
        >
          {hito.id}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base md:text-lg font-bold text-[#1F2547] truncate">{hito.name}</h3>
            <span
              className="text-[10px] font-black px-2 py-1 rounded-full text-white"
              style={{ background: hito.statusColor }}
            >
              {hito.status}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-[#6E7591] flex-wrap">
            <span><strong className="text-[#1F2547]">Monto:</strong> {hito.monto}</span>
            <span>·</span>
            <span><strong className="text-[#1F2547]">Duración:</strong> {hito.duracion}</span>
            <span>·</span>
            <span>{hito.contrato}</span>
          </div>
        </div>
        <div className="shrink-0">
          {open ? <ChevronUp size={20} className="text-[#6E7591]" /> : <ChevronDown size={20} className="text-[#6E7591]" />}
        </div>
      </button>

      {open && (
        <div className="px-5 md:px-6 pb-6 border-t border-[#F2F4FA] space-y-6 pt-5">
          <p className="text-sm text-[#4C5478] leading-relaxed">{hito.descripcion}</p>

          {/* Entregables */}
          <div>
            <h4 className="text-xs font-bold text-[#1F2547] uppercase tracking-wider mb-3">
              Entregables contractuales
            </h4>
            <div className="grid md:grid-cols-2 gap-2">
              {hito.entregables.map((e, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFBFE] border border-[#F2F4FA]"
                >
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      e.status === "ok" ? "bg-[#ECFDF5] text-[#10B981]" : "bg-[#FEF3C7] text-[#D97706]"
                    }`}
                  >
                    {e.status === "ok" ? <CheckCircle2 size={18} /> : <e.icon size={18} />}
                  </div>
                  <span className="text-sm font-semibold text-[#1F2547] flex-1">{e.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Criterios */}
          <div>
            <h4 className="text-xs font-bold text-[#1F2547] uppercase tracking-wider mb-3">
              Criterios de aceptación
            </h4>
            <ul className="space-y-1.5">
              {hito.criterios.map((c, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-[#4C5478]">
                  <CheckCircle2 size={16} className="text-[#10B981] mt-0.5 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Documentos */}
          <div>
            <h4 className="text-xs font-bold text-[#1F2547] uppercase tracking-wider mb-3">
              Documentos descargables
            </h4>
            {hito.documentos.length === 0 ? (
              <div className="p-6 text-center border-2 border-dashed border-[#E1E5F0] rounded-2xl">
                <p className="text-sm text-[#6E7591]">
                  Aún no hay documentos. Se publicarán al cierre del hito.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {hito.documentos.map((doc, idx) => (
                  <DocCard key={idx} doc={doc} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default function AdminPlanQA() {
  return (
    <div className="space-y-6" data-testid="admin-plan-qa">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-[#1F2547] text-white flex items-center justify-center">
            <ShieldCheck size={22} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-[#1F2547]">Plan QA · Entregas Contractuales</h1>
            <p className="text-sm text-[#6E7591] mt-0.5">
              Documentación oficial por hito del contrato CONT-2026-05-0002. Cada hito incluye sus entregables, criterios y documentos descargables.
            </p>
          </div>
        </div>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E1E5F0] rounded-2xl p-4">
          <p className="text-xs text-[#6E7591] font-semibold uppercase tracking-wider">Hitos totales</p>
          <p className="text-2xl font-black text-[#1F2547] mt-1">5</p>
        </div>
        <div className="bg-white border border-[#E1E5F0] rounded-2xl p-4">
          <p className="text-xs text-[#6E7591] font-semibold uppercase tracking-wider">Entregados</p>
          <p className="text-2xl font-black text-[#10B981] mt-1">1</p>
        </div>
        <div className="bg-white border border-[#E1E5F0] rounded-2xl p-4">
          <p className="text-xs text-[#6E7591] font-semibold uppercase tracking-wider">En planificación</p>
          <p className="text-2xl font-black text-[#F5B82E] mt-1">1</p>
        </div>
        <div className="bg-white border border-[#E1E5F0] rounded-2xl p-4">
          <p className="text-xs text-[#6E7591] font-semibold uppercase tracking-wider">Documentos publicados</p>
          <p className="text-2xl font-black text-[#1F2547] mt-1">
            {HITOS.reduce((acc, h) => acc + h.documentos.length, 0)}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {HITOS.map((h, i) => (
          <HitoSection key={h.id} hito={h} defaultOpen={i === 0} />
        ))}
      </div>
    </div>
  );
}
